import { KeyValue } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  AfterViewChecked,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { InputDataSourceTypes, DataTypes } from 'common/enums';
import { onErrorAlert, valueToControllerType } from 'common/helper';
import { DetailInfo, IExecuteRuleRequest } from 'common/model';
import { ToastrService } from 'ngx-toastr';
import { ExecuteRuleService } from 'shared/execute-rule.service';
import { ContactReferenceService } from 'shared/contact-reference.service';

@Component({
  selector: 'app-detail-info-type',
  templateUrl: './detail-info-type.component.html',
  standalone: false
})
export class DetailInfoTypeComponent
  implements OnInit, OnChanges, AfterViewChecked, OnDestroy
{
  @Input() detailInfo!: DetailInfo;
  @Input() formControlObj!: FormControl;
  @Input() gridView = false;
  @Input() noDefaultValue = false;

  // Optional static options for non-dynamic sources
  @Input() options: ReadonlyArray<KeyValue<string, string>> = [];

  public optionsObs: Observable<ReadonlyArray<KeyValue<string, string>>> = of(
    []
  );
  public isOptionLoading = false;

  private destroy$ = new Subject<void>();
  private detailInfoSub?: Subscription;
  private lastSig = '';
  private wasVisible = false;

  constructor(
    private alert: ToastrService,
    private executeRuleService: ExecuteRuleService,
    private contactReferenceService: ContactReferenceService,
    private el: ElementRef<HTMLElement>
  ) {}

  ngOnInit() {
    // Contact reference field: pre-fill from the non-rule generator on a NEW contact (empty field), leave it
    // editable, and DON'T run the old calculated-field rule. Existing contacts keep their stored value; other
    // fields' rules are untouched.
    if (this.detailInfo?.isReferenceField) {
      this.attachDetailInfoChangeSub();
      if (!this.hasExistingValue(this.formControlObj.value)) {
        this.contactReferenceService
          .generate()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (ref) => {
              if (ref && !this.hasExistingValue(this.formControlObj.value)) {
                this.formControlObj.patchValue(ref, { emitEvent: false });
              }
            },
            error: (err) => {
              // Non-fatal: the field stays editable/blank. Log so a failing generator is diagnosable
              // (a swallowed error here previously hid the reference not populating).
              console.error('Contact reference generation failed', err);
            }
          });
      }
      return;
    }

    if (
      !this.formControlObj.value &&
      this.detailInfo?.hasDefaultValue &&
      !this.noDefaultValue
    ) {
      if (this.detailInfo.defaultValue != null) {
        this.formControlObj.setValue(this.detailInfo.defaultValue, {
          emitEvent: false
        });
      } else if (this.detailInfo.defaultValues != null) {
        this.formControlObj.setValue(this.detailInfo.defaultValues, {
          emitEvent: false
        });
      }
    }

    this.attachDetailInfoChangeSub();

    // Initial load
    if (this.isDynamic()) {
      this.tryFetchDynamic(true);
    } else {
      Promise.resolve().then(() => (this.optionsObs = of(this.options)));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['detailInfo'] && this.detailInfo) {
      if (
        changes['detailInfo'].previousValue !==
        changes['detailInfo'].currentValue
      ) {
        this.detachDetailInfoChangeSub();
        this.attachDetailInfoChangeSub();
      }

      if (this.isDynamic()) {
        const newSig = this.buildSig(
          this.detailInfo.evideInputContentGuid,
          this.detailInfo.ruleParemeters
        );
        if (newSig !== this.lastSig) {
          this.tryFetchDynamic(true);
        }
      } else {
        Promise.resolve().then(() => (this.optionsObs = of(this.options)));
      }
    }

    if (changes['options'] && !this.isDynamic()) {
      Promise.resolve().then(() => (this.optionsObs = of(this.options)));
    }
  }

  ngAfterViewChecked() {
    const nowVisible = this.isElementVisible();
    if (nowVisible && !this.wasVisible) {
      this.wasVisible = true;
      // On show, ensure the dropdown is populated (network will be skipped if cached)
      if (this.isDynamic()) this.tryFetchDynamic(true);
    } else if (!nowVisible && this.wasVisible) {
      this.wasVisible = false;
      // Optional: clear loading state if any
      this.isOptionLoading = false;
      this.detailInfo && (this.detailInfo.isFieldLoading = false as any);
    }
  }

  ngOnDestroy() {
    this.detachDetailInfoChangeSub();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private attachDetailInfoChangeSub() {
    if (!this.detailInfo || !this.detailInfo.$valueChange) return;
    this.detailInfoSub = this.detailInfo.$valueChange
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.setChangedValue();
        if (this.isDynamic()) this.tryFetchDynamic(false);
      });
  }

  private detachDetailInfoChangeSub() {
    if (this.detailInfoSub) {
      this.detailInfoSub.unsubscribe();
      this.detailInfoSub = undefined;
    }
  }

  private isDynamic(): boolean {
    return (
      this.detailInfo?.dataSourceType === InputDataSourceTypes.DynamicSingle ||
      this.detailInfo?.dataSourceType === InputDataSourceTypes.DynamicMulti
    );
  }

  private setChangedValue() {
    if (!this.detailInfo) return;

    const current = this.formControlObj.value;
    const key = this.detailInfo.key;

    if (!current && key !== '') {
      this.formControlObj.patchValue(key, { emitEvent: false });
    }
  }

  private tryFetchDynamic(isInitial: boolean) {
    if (this.shouldSkipRuleExecution()) {
      this.setOptionLoading(false);
      this.detailInfo.isFieldLoading = false;
      return;
    }

    if (!this.detailInfo) return;

    const sig = this.buildSig(
      this.detailInfo.evideInputContentGuid,
      this.detailInfo.ruleParemeters
    );

    if (!isInitial && sig === this.lastSig) return;
    this.lastSig = sig;

    if (
      isInitial &&
      this.detailInfo.dataSourceType === InputDataSourceTypes.DynamicSingle &&
      this.noDefaultValue
    ) {
      return;
    }

    const body: IExecuteRuleRequest = {
      evideInputContentGuid: this.detailInfo.evideInputContentGuid,
      parameters: this.detailInfo.ruleParemeters || {}
    };

    this.setOptionLoading(true);
    this.detailInfo.isFieldLoading = true;

    this.executeRuleService
      .executeRule(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.setOptionLoading(false);

          if (
            this.detailInfo.dataSourceType === InputDataSourceTypes.DynamicMulti
          ) {
            const mv = res?.multiValues || {};
            const opts: KeyValue<string, string>[] = Object.keys(mv).map(
              (k) => ({
                key: k,
                value: mv[k]
              })
            );

            Promise.resolve().then(() => (this.optionsObs = of(opts)));
          } else {
            const single = res?.singleValue;
            const hasCurrentValue = this.hasExistingValue(
              this.formControlObj.value ||
              this.detailInfo.value ||
              this.detailInfo.key
            );

            if (single !== undefined && single !== null && single !== '') {
              const value = valueToControllerType(
                this.detailInfo.controllerType,
                single
              );
              if (value !== undefined && value !== null && !hasCurrentValue) {
                this.formControlObj.patchValue(value, { emitEvent: false });
              }
            }
          }

          setTimeout(() => (this.detailInfo.isFieldLoading = false), 1);
        },
        error: (err) => {
          onErrorAlert(this.alert, err);
          this.setOptionLoading(false);
          this.detailInfo.isFieldLoading = false;
        }
      });
  }

  private shouldSkipRuleExecution(): boolean {
    // The reference field is populated by the non-rule generator (see ngOnInit), never the rule engine —
    // skip rule execution for it on every path (init, ngOnChanges, value-change sub).
    if (this.detailInfo?.isReferenceField) {
      return true;
    }

    const currentValue =
      this.formControlObj.value ||
      this.detailInfo.value ||
      this.detailInfo.key;

    if (this.detailInfo?.dataType === DataTypes.DateTime) {
      if (currentValue && !this.isEmptyDateTime(currentValue)) {
        return true;
      }
    }

    if (
      this.detailInfo?.dataSourceType === InputDataSourceTypes.DynamicSingle &&
      this.hasExistingValue(currentValue)
    ) {
      return true;
    }

    return false;
  }

  private hasExistingValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0 && value.some((v) => v !== null && v !== undefined && v !== '');
    }

    // Date/Time controls (TimePicker, DateTimePicker) hold their value as a Date object, not a
    // string. The generic object check below only recognises {dataStringValue,...} shapes, so a
    // Date was treated as "empty" — which made shouldSkipRuleExecution() fail to skip, and the cell
    // re-ran its rule even when it already had a value (e.g. the attendance Start/End time the
    // server grid now supplies). Treat any valid Date as an existing value.
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    if (typeof value === 'object') {
      const objValue = value as any;
      return (
        (objValue.dataStringValue !== null &&
          objValue.dataStringValue !== undefined &&
          objValue.dataStringValue !== '') ||
        (objValue.dataStringExtraValue !== null &&
          objValue.dataStringExtraValue !== undefined &&
          objValue.dataStringExtraValue !== '')
      );
    }

    return String(value).trim() !== '';
  }

  private isEmptyDateTime(value: any): boolean {
    if (!value || value === '') return true;

    const strValue = String(value).trim();
    const emptyDatePatterns = [
      '0001-01-01',
      '01/01/0001',
      '1/1/0001',
      '0001-01-01T00:00:00',
      '0001-01-01 00:00:00',
      '01/01/0001 00:00:00',
      '01/01/0001 0:00:00 AM'
    ];

    if (emptyDatePatterns.includes(strValue)) {
      return true;
    }

    try {
      const parsedDate = new Date(strValue);
      const minDate = new Date('0001-01-01');
      return parsedDate.getTime() === minDate.getTime();
    } catch {
      return false;
    }
  }

  private setOptionLoading(loading: boolean) {
    this.isOptionLoading = loading;
    if (loading) this.formControlObj.disable({ emitEvent: false });
    else this.formControlObj.enable({ emitEvent: false });
  }

  private buildSig(guid: string, params: any): string {
    return `${guid}::${this.stableStringify(params || {})}`;
  }

  private stableStringify(obj: any): string {
    if (obj === null || obj === undefined) return '';
    if (typeof obj !== 'object') return String(obj);
    if (Array.isArray(obj))
      return `[${obj.map((v) => this.stableStringify(v)).join(',')}]`;
    const keys = Object.keys(obj).sort();
    return `{${keys
      .map((k) => `"${k}":${this.stableStringify(obj[k])}`)
      .join(',')}}`;
  }

  private isElementVisible(): boolean {
    const el = this.el.nativeElement;
    return !!(el && el.offsetParent);
  }
}








